import { useMemo } from 'react';

import {
  selectImpersonatedUserId,
  useImpersonationSelector,
} from '@revelio/auth';

import type {
  DataBuilderNewDataset,
  DataBuilderPayload,
  DataBuilderSavedDataset,
  DataBuilderUpdateDataset,
} from './data-builder-api.types';
import {
  createDataBuilderDatasetOnBackend,
  deleteDataBuilderDatasetOnBackend,
  listDataBuilderDatasetsOnBackend,
  updateDataBuilderDatasetOnBackend,
} from './datasets-backend-api';

/**
 * The view-model the DataBuilder UI consumes for a saved dataset: the generated
 * `SavedDataset`, with `payload` refined to the FE `DataBuilderPayload`.
 */
type SavedDataBuilderDataset = Omit<DataBuilderSavedDataset, 'payload'> & {
  payload: DataBuilderPayload;
};

const DATASETS_QUERY_KEY = 'data-builder-saved-datasets';

type DatasetStorage = {
  /** React Query key, scoped to the viewed user so impersonation refetches. */
  datasetsQueryKey: (string | null)[];
  listDatasets: () => Promise<SavedDataBuilderDataset[]>;
  saveDataset: (
    input: DataBuilderNewDataset
  ) => Promise<SavedDataBuilderDataset>;
  /**
   * Update an existing saved dataset in place (name + payload) by id — used by
   * the confirmation "Update & Submit" and by renaming from the list.
   */
  updateDataset: (
    input: DataBuilderUpdateDataset
  ) => Promise<SavedDataBuilderDataset>;
  deleteDataset: (id: string) => Promise<void>;
  /**
   * Whether the current user may create/update/delete datasets. False when a
   * Revelio admin is impersonating another user — their datasets are view-only.
   */
  canManageDatasets: boolean;
};

const toSavedDataset = (
  dataset: DataBuilderSavedDataset
): SavedDataBuilderDataset => ({
  ...dataset,
  payload: dataset.payload as DataBuilderPayload,
});

/**
 * Single seam for reading/writing saved DataBuilder datasets. Targets the
 * data-builder backend (`/list-datasets`, `/create-dataset`, `/update-dataset`,
 * `/delete-dataset`).
 *
 * When a Revelio admin is impersonating another user, the list is fetched for
 * that user (`?user_id=`) and management actions are disabled (view-only).
 */
export const useDatasetStorage = (): DatasetStorage => {
  const impersonatedUserId = useImpersonationSelector(selectImpersonatedUserId);

  return useMemo<DatasetStorage>(() => {
    const saveDataset = async (
      input: DataBuilderNewDataset
    ): Promise<SavedDataBuilderDataset> => {
      await createDataBuilderDatasetOnBackend(input);
      // The backend returns an empty body, so there's no id/updatedAt to echo
      // back; the caller navigates away straight after a save + submit.
      return {
        id: '',
        name: input.name,
        payload: input.payload,
        updatedAt: '',
      };
    };

    const updateDataset = async (
      input: DataBuilderUpdateDataset
    ): Promise<SavedDataBuilderDataset> => {
      await updateDataBuilderDatasetOnBackend(input);
      return {
        id: input.id,
        name: input.name,
        payload: input.payload,
        updatedAt: '',
      };
    };

    return {
      datasetsQueryKey: [DATASETS_QUERY_KEY, impersonatedUserId],
      listDatasets: async () =>
        (
          await listDataBuilderDatasetsOnBackend(
            impersonatedUserId ?? undefined
          )
        ).map(toSavedDataset),
      saveDataset,
      updateDataset,
      deleteDataset: deleteDataBuilderDatasetOnBackend,
      canManageDatasets: impersonatedUserId === null,
    };
  }, [impersonatedUserId]);
};
